// src/features/remoteAssist/components/SessionBanner.tsx

import type { RemoteAssistSession } from "../types";
import { useEndRemoteAssistSession } from "../api";
import { useRemoteAssistChannelStore } from "../store/remoteAssistStore";

interface SessionBannerProps {
  session: RemoteAssistSession;
  supporterDisplayName: string;
  vendorCompanyName?: string;
}

export function SessionBanner({ session, supporterDisplayName, vendorCompanyName }: SessionBannerProps) {
  const { mutate: endSession, isPending } = useEndRemoteAssistSession();
  const { peerStatus } = useRemoteAssistChannelStore();

  const showFinancialWarning = session.requester_scope === "platform_vendor" && session.financial_data_visible;

  if (peerStatus === "peer_disconnected") {
    return (
      <div
        role="alert"
        data-no-remote-mirror="true"
        dir="rtl"
        style={{
          position: "fixed",
          top: "64px",
          left: 0,
          right: 0,
          zIndex: 999,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          backgroundColor: "#DC2626",
          padding: "10px 20px",
          fontSize: "14px",
          fontWeight: 600,
          color: "white",
          boxShadow: "0 4px 12px rgba(220, 38, 38, 0.3)",
          fontFamily: "var(--font-family-fa, sans-serif)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>⚠️</span>
          <span>
            ارتباط با پشتیبان قطع شد — منتظر وصل مجدد... (مهلت ۵ تا ۱۰ دقیقه)
          </span>
        </div>
        <button
          type="button"
          onClick={() => endSession(session.id)}
          disabled={isPending}
          style={{
            backgroundColor: "#7F1D1D",
            color: "white",
            padding: "6px 16px",
            fontSize: "13px",
            fontWeight: 700,
            borderRadius: "8px",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            cursor: isPending ? "not-allowed" : "pointer",
          }}
        >
          {isPending ? "در حال قطع..." : "پایان کامل سشن"}
        </button>
      </div>
    );
  }

  if (peerStatus === "peer_reconnected") {
    return (
      <div
        role="alert"
        data-no-remote-mirror="true"
        dir="rtl"
        style={{
          position: "fixed",
          top: "64px",
          left: 0,
          right: 0,
          zIndex: 999,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          backgroundColor: "#2563EB",
          padding: "10px 20px",
          fontSize: "14px",
          fontWeight: 600,
          color: "white",
          boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
          fontFamily: "var(--font-family-fa, sans-serif)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>✓</span>
          <span>ارتباط با پشتیبان دوباره برقرار شد.</span>
        </div>
      </div>
    );
  }

  return (
    <div
      role="alert"
      data-no-remote-mirror="true"
      dir="rtl"
      style={{
        position: "fixed",
        top: "64px",
        left: 0,
        right: 0,
        zIndex: 999,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        background: "linear-gradient(90deg, #D97706, #B45309)",
        padding: "10px 20px",
        fontSize: "14px",
        fontWeight: 600,
        color: "white",
        boxShadow: "0 4px 14px rgba(180, 83, 9, 0.3)",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "1.1rem" }}>🤝</span>
        <span>
          سشن راهنمایی از راه دور فعال است — در حال حاضر <strong>{supporterDisplayName}</strong>
          {vendorCompanyName ? ` از شرکت «${vendorCompanyName}»` : ""} صفحه شما را مشاهده می‌کند.
        </span>
        {showFinancialWarning && (
          <span
            style={{
              padding: "2px 8px",
              borderRadius: "6px",
              background: "#991B1B",
              fontSize: "12px",
              fontWeight: 700,
            }}
          >
            ⚠️ داده‌های مالی برای پشتیبان قابل مشاهده است
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => endSession(session.id)}
        disabled={isPending}
        style={{
          backgroundColor: "#78350F",
          color: "white",
          padding: "6px 16px",
          fontSize: "13px",
          fontWeight: 700,
          borderRadius: "8px",
          border: "1px solid rgba(255, 255, 255, 0.4)",
          cursor: isPending ? "not-allowed" : "pointer",
          flexShrink: 0,
          transition: "background 0.2s",
        }}
      >
        {isPending ? "در حال قطع..." : "پایان سشن"}
      </button>
    </div>
  );
}
